import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Avatar,
  Collapse,
  IconButton,
  Paper,
  Button,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { getPlayerActivities } from "../features/activity/activitySlice";

// Helper function to format dates
const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-GB"); // DD/MM/YYYY, HH:MM:SS format
};

// Helper function to get action color
const getActionColor = (actionType) => {
  switch (actionType) {
    case "player_created":
    case "entry_created":
      return "success";
    case "player_updated":
    case "entry_updated":
      return "info";
    case "player_deleted":
    case "entry_deleted":
      return "error";
    default:
      return "default";
  }
};

// Helper function to get entity icon
const getEntityIcon = (entityType) => {
  switch (entityType) {
    case "player":
      return <PersonIcon />;
    case "entry":
      return <EventIcon />;
    default:
      return <PersonIcon />;
  }
};

function ActivityTable({ playerId, compact = false, maxItems = null }) {
  const dispatch = useDispatch();
  const activities = useSelector((state) => state.activity.activities);
  const isLoading = useSelector((state) => state.activity.isLoading);

  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchActivities = async () => {
    if (playerId) {
      await dispatch(getPlayerActivities(playerId));
    }
  };

  useEffect(() => {
    if (playerId) {
      fetchActivities();
    }
  }, [playerId]);

  const toggleRow = (activityId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(activityId)) {
      newExpanded.delete(activityId);
    } else {
      newExpanded.add(activityId);
    }
    setExpandedRows(newExpanded);
  };

  if (isLoading) {
    return compact ? (
      <Typography variant="body2" color="text.secondary">
        Loading activities...
      </Typography>
    ) : (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Activity Log
          </Typography>
          <Typography color="text.secondary">Loading activities...</Typography>
        </CardContent>
      </Card>
    );
  }

  // Compact version for sidebar
  if (compact) {
    return (
      <Box>
        {activities?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No recent activities
          </Typography>
        ) : (
          <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
            {activities?.map((activity) => (
              <Card
                key={activity.id}
                variant="outlined"
                sx={{
                  mb: 1,
                  p: 1,
                  "&:last-child": { mb: 0 },
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 24, height: 24, p: 2 }}>
                    {getEntityIcon(activity.entity_type)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        fontWeight: "medium",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {activity.description}
                    </Typography>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Chip
                        label={activity.action_type?.replace(/_/g, " ")}
                        size="small"
                        variant="outlined"
                        color={getActionColor(activity.action_type)}
                        sx={{
                          height: 20,
                          fontSize: "0.6rem",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(activity.created_at)?.split(",")[0]}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">
            Activity Log ({activities.length})
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchActivities()}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {activities.length === 0 ? (
          <Typography color="text.secondary">
            No activities found for this player.
          </Typography>
        ) : (
          <Paper elevation={0} sx={{ border: 1, borderColor: "divider" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={50}></TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Date & Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activities.map((activity) => (
                  <>
                    <TableRow key={activity.id} hover>
                      <TableCell>
                        {activity.changes && (
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(activity.id)}
                          >
                            {expandedRows.has(activity.id) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={activity.action_type
                            .replace("_", " ")
                            .toUpperCase()}
                          color={getActionColor(activity.action_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            {getEntityIcon(activity.entity_type)}
                          </Avatar>
                          <Typography
                            variant="body2"
                            textTransform="capitalize"
                          >
                            {activity.entity_type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {activity.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {activity.user_name || activity.user_id || "System"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(activity.created_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {activity.changes && expandedRows.has(activity.id) && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0 }}>
                          <Collapse
                            in={expandedRows.has(activity.id)}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Changes:
                              </Typography>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1,
                                  bgcolor: "background.paper",
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <pre
                                  style={{
                                    margin: 0,
                                    fontSize: "12px",
                                    whiteSpace: "pre-wrap",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {JSON.stringify(
                                    JSON.parse(activity.changes),
                                    null,
                                    2
                                  )}
                                </pre>
                              </Paper>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
}

export default ActivityTable;
